import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from 'src/shared/api/api';
import { AdProductGroup } from 'src/shared/models/account';
import {
  AccountDetailsResponse,
  BillingDetailsResponse,
  ChooseAccountsContextResponse,
  CombinationInvitationUpdateStatus,
  CombineAccountsResponse,
  EditedAccountDetails,
  ManagerAccountChildrenResponse,
  QueryCombinationInvitationsResponse,
  ReviewAccountsResponse,
  UpdateCombinationInvitationResponse,
  UpgradeAccountsResponse,
  UpgradeStatusResponse,
} from './types';

const BASE_URL = '/ads-account/api/v1/combine/';
const REDUCER_PATH = 'CombineAccountsApi';

export const combineAccountsApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: axiosBaseQuery({ baseUrl: BASE_URL }),
  tagTypes: ['ReviewAccounts'],
  endpoints: (builder) => ({
    getChooseAccountsContext: builder.query<
      ChooseAccountsContextResponse,
      | {
          query?: string;
          adProductGroup?: AdProductGroup;
          paginationToken?: string;
          maxResults?: number;
        }
      | undefined
    >({
      query: (params) => ({
        url: 'choose-accounts-context',
        method: 'GET',
        params: params
          ? {
              ...(params.query !== undefined && { query: params.query }),
              ...(params.adProductGroup !== undefined && {
                adProductGroup: params.adProductGroup,
              }),
              ...(params.paginationToken !== undefined && {
                paginationToken: params.paginationToken,
              }),
              ...(params.maxResults !== undefined && {
                maxResults: params.maxResults,
              }),
            }
          : undefined,
      }),
    }),

    getManagerAccountChildren: builder.query<
      ManagerAccountChildrenResponse,
      {
        managerAccountId: string;
        paginationToken?: string;
        maxResults?: number;
        adProductGroup?: AdProductGroup;
      }
    >({
      query: ({
        managerAccountId,
        paginationToken,
        maxResults,
        adProductGroup,
      }) => ({
        url: `manager-account/${managerAccountId}/children`,
        method: 'GET',
        params: {
          ...(paginationToken !== undefined && { paginationToken }),
          ...(maxResults !== undefined && { maxResults }),
          ...(adProductGroup !== undefined && { adProductGroup }),
        },
      }),
    }),

    getAccountDetails: builder.query<
      AccountDetailsResponse,
      { globalAccountId: string; cfid: string }
    >({
      query: ({ globalAccountId, cfid }) => ({
        url: 'account-details',
        method: 'GET',
        params: { globalAccountId, cfid },
      }),
    }),

    validateAccountCombination: builder.query<
      {
        eligible: boolean;
        errorCode?: string | null;
        errorMessage?: string | null;
      },
      { globalAccountId: string; cfid: string }
    >({
      query: ({ globalAccountId, cfid }) => ({
        url: 'validate',
        method: 'GET',
        params: { saAccountId: globalAccountId, dspAccountId: cfid },
      }),
    }),

    getReviewAccounts: builder.query<
      ReviewAccountsResponse,
      { globalAccountId: string; cfid: string }
    >({
      query: ({ globalAccountId, cfid }) => ({
        url: 'review-accounts',
        method: 'GET',
        params: { globalAccountId, cfid },
      }),
      providesTags: ['ReviewAccounts'],
    }),

    getBillingDetails: builder.query<
      BillingDetailsResponse,
      { globalAccountId: string; cfid: string }
    >({
      // The Manager Account side is resolved server-side from the DSP
      // account (cfid) via the AMACS account hierarchy.
      query: ({ globalAccountId, cfid }) => ({
        url: 'billing-details',
        method: 'GET',
        params: { globalAccountId, cfid },
      }),
    }),

    createAccountCombination: builder.mutation<
      CombineAccountsResponse,
      {
        dspAccountId: string;
        sponsoredAdsAccountId?: string;
        selectedAccountId?: string;
        editedAccountDetails?: EditedAccountDetails;
      }
    >({
      query: ({
        dspAccountId,
        sponsoredAdsAccountId,
        selectedAccountId,
        editedAccountDetails,
      }) => ({
        url: '../ads-account/combine',
        method: 'POST',
        data: {
          dspAccountId,
          sponsoredAdsAccountId,
          selectedAccountId,
          ...(editedAccountDetails && { editedAccountDetails }),
        },
        ...(selectedAccountId && {
          headers: { 'Amazon-Advertising-API-AdvertiserId': selectedAccountId },
        }),
      }),
      invalidatesTags: ['ReviewAccounts'],
    }),

    /**
     * Upgrades each listed Amazon DSP account into its own global Advertiser account.
     *
     * Its own endpoint rather than a mode of combine, because the two are different operations:
     * combine merges one DSP account into one existing target and has a single result, while an
     * upgrade has no target and creates one new account per listed account. This previously posted
     * to the combine path, whose model declares a singular `dspAccountId` and no `upgrade` field,
     * so the array bound to nothing, `dspAccountId` arrived null, and the request was refused by
     * the combine guard before reaching GRS.
     *
     * Answers 202. An accepted account means GRS took the request, never that the account has been
     * upgraded: the combination runs asynchronously afterwards.
     */
    upgradeAccounts: builder.mutation<
      UpgradeAccountsResponse,
      { dspAccountIds: string[] }
    >({
      query: ({ dspAccountIds }) => ({
        url: '../ads-account/upgrade',
        method: 'POST',
        data: { dspAccountIds },
      }),
    }),
    /**
     * Progress of one account's upgrade, keyed on the DSP account id.
     *
     * Its own endpoint rather than the existing account-status one, because that is keyed on the
     * global Advertiser account id and an upgrade creates a new account whose id the caller never
     * learns. ARMS bridges the gap internally, resolving the DSP account to the global account and
     * then reading its status, so none of that resolution is visible here.
     *
     * `resolved: false` and `PARTIALLY_CREATED` both mean still upgrading. CREATED is the only
     * terminal value: no failure status exists, so a caller must impose its own deadline rather
     * than waiting for one.
     *
     * Batched: the whole outstanding set goes in one call per round, which suits an operation that
     * upgrades a list of accounts. Entries come back keyed on `dspAccountId` and must be matched on
     * it rather than on position, and a failure is reported per entry so one unreadable account
     * cannot blank the rest.
     *
     * A POST despite being a read. A batch of ids is too long for a query string, where the
     * rejection arrives as an opaque 414 from upstream of the service rather than a validated error
     * naming the cap; the body also reuses the same `dspAccountIds` shape the upgrade call already
     * sends, and a POST is not cached by intermediaries, which matters for a value that goes stale.
     *
     * Modelled as a mutation rather than a query purely so it is never served from the store's
     * cache. Polling sends identical arguments whenever a round settles nothing, and a query would
     * answer those rounds from cache instead of asking the service, which defeats the point.
     */
    fetchUpgradeStatus: builder.mutation<
      UpgradeStatusResponse,
      { dspAccountIds: string[] }
    >({
      query: ({ dspAccountIds }) => ({
        url: '../ads-account/upgrade/status',
        method: 'POST',
        data: { dspAccountIds },
      }),
    }),
    /**
     * The account-combination invitations visible to the caller, in both directions.
     *
     * A GET because it is a read, so it carries no CSRF header. The caller is taken from the
     * session's internal-gateway headers rather than a parameter, so this cannot be pointed at
     * another customer.
     *
     * **Answers an empty list in every environment today.** GRS's QueryAccountCombinationInvitation
     * is still a no-op placeholder that returns an empty response, so the Requests table has no
     * rows against a real backend and can only be exercised against the local mock. Nothing here
     * needs to change when GRS implements it.
     *
     * Deliberately carries no cache tag. The table pages through this endpoint and re-reads from
     * the first page after an answer, because answering changes what the earlier pages hold; a tag
     * would refetch only whichever page's arguments were used last, leaving the accumulated rows
     * disagreeing with it. One refresh mechanism, owned by the caller.
     */
    getCombinationInvitations: builder.query<
      QueryCombinationInvitationsResponse,
      { paginationToken?: string; maxResults?: number } | undefined
    >({
      query: (params) => ({
        url: 'invitations',
        method: 'GET',
        params: params
          ? {
              ...(params.paginationToken !== undefined && {
                paginationToken: params.paginationToken,
              }),
              ...(params.maxResults !== undefined && {
                maxResults: params.maxResults,
              }),
            }
          : undefined,
      }),
    }),

    /**
     * Answers one pending invitation: approve, reject, or send again.
     *
     * Rejecting covers two user-facing actions, because GRS distinguishes them by caller identity
     * rather than by status: the original requester withdrawing their own request revokes it, and
     * any other admin refusing one declines it.
     *
     * Answers **202** for an approval and 200 otherwise, because approving is the only one that is
     * not finished when the call returns: it triggers the combination, which GRS runs
     * asynchronously. A success here therefore means the answer was accepted, not that the accounts
     * have been combined.
     */
    updateCombinationInvitation: builder.mutation<
      UpdateCombinationInvitationResponse,
      {
        accountCombinationInvitationId: string;
        status: CombinationInvitationUpdateStatus;
      }
    >({
      query: ({ accountCombinationInvitationId, status }) => ({
        url: '../ads-account/combine/update-invitation',
        method: 'POST',
        data: { accountCombinationInvitationId, status },
      }),
    }),
  }),
});

export const {
  useGetChooseAccountsContextQuery,
  useLazyGetChooseAccountsContextQuery,
  useLazyGetManagerAccountChildrenQuery,
  useGetAccountDetailsQuery,
  useLazyGetAccountDetailsQuery,
  useValidateAccountCombinationQuery,
  useLazyValidateAccountCombinationQuery,
  useGetReviewAccountsQuery,
  useGetBillingDetailsQuery,
  useCreateAccountCombinationMutation,
  useUpgradeAccountsMutation,
  useFetchUpgradeStatusMutation,
  useGetCombinationInvitationsQuery,
  useLazyGetCombinationInvitationsQuery,
  useUpdateCombinationInvitationMutation,
} = combineAccountsApi;
